// A minute in a recording, as the hub writes it and reads it back. Pure, so a test can import it.
//
// Links: the hub writes ?t=08m51s, and ?t=1h01m41s from an hour on (the critic's settlement, 3 Oct:
// FEWeek.dc.html's own slug turns 1:01:41 into 1m01m41s, which is wrong). Reading, it takes every
// form a link to a minute has had: plain seconds (?t=531, search's links), 45m33s (the seeded
// question), 1h01m41s, the board's 1m01m41s, a clock (08:51, 1:01:41) and Vimeo's 531s.
//
// On the page: chapter times as the boards write them, 03:48 under an hour and 1:01:41 from an hour
// on (FEWeek.dc.html), and a key learning's range as "03:48 to 08:31".

const pad = n => String(n).padStart(2, '0')
const whole = s => Math.max(0, Math.floor(Number(s) || 0))

// 531 -> '08m51s', 3701 -> '1h01m41s', 9 -> '00m09s'
export function linkStamp(seconds) {
  const s = whole(seconds)
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60
  return h ? `${h}h${pad(m)}m${pad(r)}s` : `${pad(m)}m${pad(r)}s`
}

// 531 -> '08:51', 3701 -> '1:01:41'
export function chapterTime(seconds) {
  const s = whole(seconds)
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60
  return h ? `${h}:${pad(m)}:${pad(r)}` : `${pad(m)}:${pad(r)}`
}

// '03:48 to 08:31'
export function rangeLine(start, end) {
  if (end == null || end <= start) return chapterTime(start)
  return `${chapterTime(start)} to ${chapterTime(end)}`
}

// Any form above as seconds, or null when it isn't a minute at all
export function parseStamp(value) {
  if (value == null) return null
  const s = String(value).trim().toLowerCase()
  if (!s) return null
  if (/^\d{1,6}s?$/.test(s)) return Number(s.replace('s', ''))
  // a clock: 08:51, 1:01:41, 01:02:09
  let m = /^(?:(\d{1,2}):)?(\d{1,2}):(\d{2})$/.exec(s)
  if (m) {
    const [, h, mm, ss] = m
    if (Number(ss) > 59) return null
    return (Number(h || 0) * 3600) + Number(mm) * 60 + Number(ss)
  }
  // units: 1h01m41s, 45m33s, 41s, 2m, and the board's 1m01m41s (minutes twice: the first is hours)
  m = /^(?:(\d{1,2})h)?(?:(\d{1,3})m)?(?:(\d{1,3})m)?(?:(\d{1,4})s)?$/.exec(s)
  if (m && (m[1] || m[2] || m[3] || m[4])) {
    let [, h, m1, m2, sec] = m
    if (m2 != null) { if (h != null) return null; h = m1; m1 = m2 }
    return Number(h || 0) * 3600 + Number(m1 || 0) * 60 + Number(sec || 0)
  }
  return null
}

// The chapter a second falls in: the last that starts at or before it, or null before the first
export function chapterAt(chapters, seconds) {
  let hit = null
  for (const c of chapters || []) {
    if (c.start_seconds <= seconds) hit = c
    else break
  }
  return hit
}

// A key learning's body, with its [mm:ss] and [hh:mm:ss] marks found, so each can seek the player:
// [{ text }, { stamp: '04:18', seconds: 258 }, ...]
export function splitStamps(text) {
  const out = []
  const re = /\[((?:\d{1,2}:)?\d{1,2}:\d{2})\]/g
  let last = 0
  let m
  while ((m = re.exec(text))) {
    const seconds = parseStamp(m[1])
    if (seconds == null) continue
    if (m.index > last) out.push({ text: text.slice(last, m.index) })
    out.push({ stamp: m[1], seconds })
    last = m.index + m[0].length
  }
  if (last < text.length) out.push({ text: text.slice(last) })
  return out
}
